import {
  ActionCommand,
  dayMonthDateString,
  Shift,
  shiftTimeRangeDurationString,
} from '@nextforce/api'
import { ListItem, makeStyles, Text } from '@rneui/themed'
import { font } from 'app/theme'
import { t } from 'app/i18l'
import React, { useState } from 'react'
import { FlatList, Pressable, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { CommandButton } from 'shared/components/command_button'
import { RoundedButton } from 'shared/components/rounded-button'

interface Props {
  shifts: Shift[] | undefined
  onSelectShift: (shift: Shift, command: ActionCommand) => Promise<void>
  onBack: () => Promise<void>
  command?: ActionCommand
}

export function CrewShiftsView({
  shifts,
  onSelectShift,
  onBack,
  command,
}: Props) {
  const styles = useStyles()

  const [selectedShift, setSelectedShift] = useState<Shift | undefined>()

  if (!shifts) {
    return null
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.container}>
      {!shifts.length ? (
        <View style={styles.card}>
          <Text style={styles.noShiftsText}>{t('noShiftsNoSwap')}</Text>
        </View>
      ) : (
        <Text style={styles.title}>{`${
          shifts[0].user?.firstName ?? 'User'
        }'s Shifts`}</Text>
      )}
      <FlatList
        style={styles.list}
        data={shifts}
        keyExtractor={item => String(item.id)}
        renderItem={({ item }) => (
          <ShiftRow
            shift={item}
            selected={selectedShift && item.id === selectedShift.id}
            onPress={() => setSelectedShift(item)}
          />
        )}
      />
      {!shifts.length ? (
        <RoundedButton
          containerStyle={styles.buttonContainer}
          title={t('selectDifferentCrew')}
          onPress={onBack}
          emphasized
        />
      ) : (
        command &&
        selectedShift && (
          <CommandButton
            command={command}
            containerStyle={styles.buttonContainer}
            onPress={() => onSelectShift(selectedShift, command)}
          />
        )
      )}
    </SafeAreaView>
  )
}

interface ShiftRowProps {
  shift: Shift
  selected?: boolean
  onPress(): void
}

function ShiftRow({ shift, selected, onPress }: ShiftRowProps) {
  const styles = useStyles()
  return (
    <Pressable onPress={onPress}>
      <ListItem containerStyle={styles.listItem}>
        <ListItem.Content>
          <ListItem.Title>
            {dayMonthDateString(shift.date)} {` (${shift.store.storeNumber})`}
          </ListItem.Title>
          <ListItem.Title>{shiftTimeRangeDurationString(shift)}</ListItem.Title>
          <ListItem.Title>
            {shift.position ? shift.position : t('anyPosition')}
          </ListItem.Title>
        </ListItem.Content>
        <ListItem.CheckBox
          onPress={onPress}
          iconRight
          checkedIcon="dot-circle-o"
          uncheckedIcon="circle-o"
          checked={selected ?? false}
        />
      </ListItem>
    </Pressable>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    justifyContent: 'space-between',
    backgroundColor: theme.colors?.grey5,
  },

  title: {
    margin: 10,
    fontSize: 15,
  },
  card: {
    justifyContent: 'center',
    backgroundColor: theme.colors?.white,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 3,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    paddingHorizontal: 10,
    paddingVertical: 20,
    marginTop: 10,
  },

  noShiftsText: {
    fontSize: 18,
  },

  list: {
    paddingBottom: 10,
  },

  listItem: {
    marginVertical: 5,
  },
  date: {
    fontSize: 19,
    fontFamily: font.bold,
    margin: 10,
    marginBottom: 5,
  },
  time: { fontSize: 16, marginLeft: 10, color: theme.colors?.grey1 },
  shiftPosition: { fontSize: 17, margin: 10 },
  buttonContainer: {
    margin: 10,
  },
}))
